/**
 * Issue #7 / #8 — 导出 API 客户端（真实后端接口）。
 *
 * 类型契约的唯一来源(SSOT)是 apps/api/openapi.yaml，经 `pnpm codegen` 生成到
 * ./generated/types.ts；本模块只做派生，禁止手写字段定义。
 *
 * 红线（AI GUIDE / Agent.md）：**禁止前端假导出**——不得用当前页 state 拼接文件。
 * 两个导出都走后端真实接口；ApiClient.download 强制校验 HTTP 状态 + content-type，
 * 非 2xx / 非预期类型一律抛错，绝不把 HTML 错误页当成 .md/.csv 存盘。
 */
import { apiClient } from './api';
import { triggerBrowserDownload } from './download';
import type { operations } from './generated/types';

/** 通话导出的 query 参数（由 openapi 生成的 operation 派生，与列表筛选同源）。 */
export type CallsExportQuery = NonNullable<
  operations['exportCalls']['parameters']['query']
>;

export const COURSE_EXPORT_CONTENT_TYPE = 'text/markdown';
export const CALLS_EXPORT_CONTENT_TYPE = 'text/csv';

/**
 * Issue #7：导出课程为 Markdown 并触发浏览器下载。
 * 文件名以服务端 `Content-Disposition`（RFC 5987 `filename*`，中文可读）为准。
 */
export async function exportCourseMarkdown(
  courseId: string,
  fallbackName = 'course.md',
): Promise<string> {
  const { blob, fileName } = await apiClient.download(
    `/admin/courses/${encodeURIComponent(courseId)}/export`,
    {
      expectContentType: COURSE_EXPORT_CONTENT_TYPE,
      fallbackFileName: fallbackName,
    },
  );
  triggerBrowserDownload(blob, fileName);
  return fileName;
}

/**
 * Issue #8：按**当前筛选条件**导出通话记录 CSV 并触发下载。
 *
 * 关键：调用方传入的就是列表正在用的那份筛选条件，但 `page` / `limit` 必须被剔除——
 * 导出范围是「当前筛选下的全部记录」，不是当前页。后端同样会忽略分页参数（双保险），
 * 这里剔除是为了让请求 URL 本身就表达正确语义。
 */
export async function exportCallsCsv(filters: CallsExportQuery): Promise<string> {
  const { blob, fileName } = await apiClient.download('/calls/export', {
    query: filters as unknown as Record<string, unknown>,
    expectContentType: CALLS_EXPORT_CONTENT_TYPE,
    fallbackFileName: '通话记录.csv',
  });
  triggerBrowserDownload(blob, fileName);
  return fileName;
}

/**
 * 从列表筛选条件派生导出参数：**剔除分页字段**，其余筛选原样保留。
 * 纯函数，便于单测锁死「导出用的是当前筛选、且不带分页」这条契约。
 */
export function toCallsExportQuery(
  filters: Record<string, unknown>,
): CallsExportQuery {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'page' || key === 'limit') continue;
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out as CallsExportQuery;
}
